import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Params, RouterModule, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { GableDrawerService } from '../../../services/gable/gable-drawer.service';
import {
    GableWorkflow,
    GableWorkflowRun,
    GableWorkflowsService,
} from '../../../services/gable/gable-workflows.service';
import {
    formatDuration,
    formatInTimezone,
    inFlightText,
    isWaitingOnCapacity,
    latestRun,
    outcomesText,
    relativeTime,
    runOutcomeText,
    runSummary,
} from '../../../services/gable/gable-workflow-format';
import { GrowlerService } from '../../shared/growler/growler.service';
import { ConfirmDialogComponent } from '../../v2-layout/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { GableWorkflowEditorComponent } from './gable-workflow-editor.component';
import { focusWhenMounted } from '../focus-when-mounted';

type WorkflowMenuAction = 'run' | 'runs' | 'toggle' | 'edit' | 'delete';

interface WorkflowMenuItem {
    value: WorkflowMenuAction;
    label: string;
    icon?: string;
    disabled?: boolean;
    destructive?: boolean;
}

export type GableWorkflowsPanelView = 'list' | 'editor' | 'runs';

function isInFlight(run: GableWorkflowRun | null | undefined): run is GableWorkflowRun {
    return run?.status === 'queued' || run?.status === 'running';
}

/** Core embeds each workflow's latest three runs in the list; in-memory patches keep the same cap. */
const EMBEDDED_RUNS_CAP = 3;
const RUN_NOW_EVENT_TOOLTIP = 'Runs only when its trigger happens.';

/** Gable drawer WORKFLOWS panel (SD-9963): org admins tell Gable what to do, when, and what happens after. */
@UntilDestroy()
@Component({
    selector: 'app-gable-workflows-panel',
    templateUrl: './gable-workflows-panel.component.html',
    styleUrls: ['./gable-workflows-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule, ConfirmDialogComponent, GableWorkflowEditorComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GableWorkflowsPanelComponent implements OnInit {
    view: GableWorkflowsPanelView = 'list';
    workflows: GableWorkflow[] = [];
    loading = false;
    listLoaded = false;
    listLoadFailed = false;

    editing: GableWorkflow | null = null;

    runsWorkflow: GableWorkflow | null = null;
    runs: GableWorkflowRun[] = [];
    runsTotal = 0;
    runsPage = 0;
    runsLoading = false;
    runsLoadFailed = false;
    expandedRunIds = new Set<number>();

    removeConfirmOpen = false;
    removingWorkflow: GableWorkflow | null = null;

    readonly timezone = LegFiJwtService.getTimezone();

    private readonly _busy = new Set<number>();

    constructor(
        private readonly _workflows: GableWorkflowsService,
        private readonly _growler: GrowlerService,
        private readonly _drawer: GableDrawerService,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _router: Router,
    ) {}

    /** A finished run is patched into its card in memory; nothing here reloads the list. */
    ngOnInit(): void {
        this._drawer.workflowRunFinished$.pipe(untilDestroyed(this)).subscribe(({ workflowId, run }) => {
            const w = this.workflows.find((x) => x.id === workflowId);
            if (w) {
                this._replace({
                    ...w,
                    runs: [run, ...(w.runs ?? []).filter((r) => r.id !== run.id)].slice(0, EMBEDDED_RUNS_CAP),
                    lastRunAt: run.startedAt ?? run.createdAt ?? w.lastRunAt,
                });
            }
            if (this.runsWorkflow?.id === workflowId) {
                this.runs = this.runs.map((r) => (r.id === run.id ? run : r));
            }
            this._cdr.markForCheck();
        });
        this.loadList();
    }

    get isSuperUser(): boolean {
        return LegFiJwtService.read()?.superUser === true;
    }

    get panelEmpty(): boolean {
        return this.listLoaded && !this.loading && !this.listLoadFailed && this.workflows.length === 0;
    }

    get runsHaveMore(): boolean {
        return this.runs.length < this.runsTotal;
    }

    /** A failed reload of an already-shown list keeps that list and growls; only a first load falls to the error state. */
    loadList(): void {
        const reload = this.listLoaded && !this.listLoadFailed;
        this.loading = true;
        this.listLoadFailed = false;
        this._cdr.markForCheck();
        this._workflows.list().pipe(untilDestroyed(this)).subscribe({
            next: ({ workflows }) => {
                this.workflows = workflows ?? [];
                this.loading = false;
                this.listLoaded = true;
                this._watchInFlightRuns();
                this._cdr.markForCheck();
            },
            error: (err: HttpErrorResponse) => {
                this.loading = false;
                this.listLoaded = true;
                if (reload) {
                    this._growler.fromErrorResponse(err, 'Could not refresh workflows.');
                } else {
                    this.listLoadFailed = true;
                }
                this._cdr.markForCheck();
            },
        });
    }

    isBusy(w: GableWorkflow): boolean {
        return this._busy.has(w.id);
    }

    skillName(w: GableWorkflow): string | null {
        return w.skill?.name ?? null;
    }

    cadence(w: GableWorkflow): string {
        return w.trigger?.cadenceLabel ?? '';
    }

    isEventTriggered(w: GableWorkflow): boolean {
        return w.trigger?.type === 'event';
    }

    triggerIcon(w: GableWorkflow): string {
        return this.isEventTriggered(w) ? 'lightning' : 'calendar-blank';
    }

    /** Run now only makes sense for a schedule; an event workflow waits for its trigger. */
    runNowTooltip(w: GableWorkflow): string {
        return this.isEventTriggered(w) ? RUN_NOW_EVENT_TOOLTIP : 'Run now';
    }

    /** Batch runs from the daily sweep carry a count instead of one entity. */
    runTriggerCount(run: GableWorkflowRun): number | null {
        return typeof run.triggerCount === 'number' && run.triggerCount > 0 ? run.triggerCount : null;
    }

    outcomes(w: GableWorkflow): string {
        return outcomesText(w.outcomes);
    }

    nextRun(w: GableWorkflow): string {
        if (w.status === 'paused') {
            return 'Paused';
        }
        if (w.trigger?.type === 'event') {
            return 'Runs when it happens';
        }
        const when = formatInTimezone(w.nextRunAt, this.timezone);
        return when ? `Next run ${when}` : 'Next run not set';
    }

    lastRunSummary(w: GableWorkflow): string {
        return runSummary(latestRun(w));
    }

    /**
     * The card's last line. The badges beside it already say running, failed or
     * paused, so the words do not repeat them.
     */
    lastRunLine(w: GableWorkflow): string {
        if (this.isRunning(w)) {
            return this.runningLine(w);
        }
        const summary = this.lastRunFailed(w)
            ? this.lastRunSummary(w).replace(/^Failed(: )?/, '').replace(/^ · /, '')
            : this.lastRunSummary(w);
        const tail = this.isEventTriggered(w) || w.status === 'paused' ? '' : `. ${this.nextRun(w)}`;
        return `${summary || 'Failed'}${tail}`;
    }

    lastRunFailed(w: GableWorkflow): boolean {
        return latestRun(w)?.status === 'failed';
    }

    isRunning(w: GableWorkflow): boolean {
        return isInFlight(latestRun(w));
    }

    /** The badge carries the state, so the line beside it only says what happens next. */
    runningBadge(w: GableWorkflow): string {
        const run = latestRun(w);
        if (run?.status === 'running') {
            return 'Running';
        }
        return isWaitingOnCapacity(run) ? 'Waiting' : 'Queued';
    }

    runningLine(w: GableWorkflow): string {
        return isWaitingOnCapacity(latestRun(w))
            ? 'Waiting, Gable is busy. It runs when there is room.'
            : 'You will see what it found when it finishes.';
    }

    statusLabel(w: GableWorkflow): string {
        return w.status === 'paused' ? 'Paused' : 'Active';
    }

    workflowStatus(w: GableWorkflow): 'completed' | 'disabled' {
        return w.status === 'paused' ? 'disabled' : 'completed';
    }

    /** ph-status-badge's four states stand in for completed/failed/running(pending)/queued-or-waiting(disabled). */
    runStatus(run: GableWorkflowRun): 'completed' | 'failed' | 'pending' | 'disabled' {
        if (run.status === 'completed') { return 'completed'; }
        if (run.status === 'failed') { return 'failed'; }
        if (run.status === 'running') { return 'pending'; }
        return 'disabled';
    }

    rowMenuItems(w: GableWorkflow): WorkflowMenuItem[] {
        return [
            ...(this.isSuperUser ? [{ value: 'run' as const, label: 'Run now', icon: 'play', disabled: this.isBusy(w) || this.isRunning(w) || this.isEventTriggered(w) }] : []),
            { value: 'runs', label: 'View runs', icon: 'clock-counter-clockwise' },
            { value: 'toggle', label: w.status === 'paused' ? 'Resume' : 'Pause', icon: w.status === 'paused' ? 'play' : 'pause' },
            { value: 'edit', label: 'Edit', icon: 'pencil-simple' },
            { value: 'delete', label: 'Delete', icon: 'trash', destructive: true },
        ];
    }

    detailMenuItems(w: GableWorkflow): WorkflowMenuItem[] {
        return this.rowMenuItems(w).filter((i) => i.value !== 'run' && i.value !== 'runs');
    }

    onRowMenuSelect(w: GableWorkflow, action: WorkflowMenuAction): void {
        if (action === 'run') { this.runNow(w); }
        else if (action === 'runs') { this.openRuns(w); }
        else if (action === 'toggle') { this.togglePause(w); }
        else if (action === 'edit') { this.openEdit(w); }
        else if (action === 'delete') { this.confirmRemove(w); }
    }

    openCreate(): void {
        this.editing = null;
        this.view = 'editor';
        this._focusView();
    }

    openEdit(w: GableWorkflow): void {
        this.editing = w;
        this.view = 'editor';
        this._focusView();
    }

    onEditorSaved(w: GableWorkflow): void {
        const exists = this.workflows.some((x) => x.id === w.id);
        this.workflows = exists ? this.workflows.map((x) => (x.id === w.id ? w : x)) : [w, ...this.workflows];
        this.backToList();
    }

    backToList(): void {
        this.view = 'list';
        this.editing = null;
        this.runsWorkflow = null;
        this._focusView('[data-test="new-workflow"]');
    }

    openRuns(w: GableWorkflow): void {
        this.runsWorkflow = w;
        this.runs = [];
        this.runsTotal = 0;
        this.runsPage = 0;
        this.runsLoadFailed = false;
        this.expandedRunIds = new Set();
        this.view = 'runs';
        this._focusView();
        this.loadMoreRuns();
    }

    loadMoreRuns(): void {
        const w = this.runsWorkflow;
        if (!w || this.runsLoading) {
            return;
        }
        const page = this.runsPage + 1;
        this.runsLoading = true;
        this.runsLoadFailed = false;
        this._cdr.markForCheck();
        this._workflows.runs(w.id, page).pipe(untilDestroyed(this)).subscribe({
            next: ({ runs, total }) => {
                if (this.runsWorkflow?.id !== w.id) {
                    return;
                }
                const seen = new Set(this.runs.map((r) => r.id));
                this.runs = [...this.runs, ...(runs ?? []).filter((r) => !seen.has(r.id))];
                this.runsTotal = Math.max(total ?? 0, this.runs.length);
                this.runsPage = page;
                this.runsLoading = false;
                this._cdr.markForCheck();
            },
            error: () => {
                this.runsLoading = false;
                this.runsLoadFailed = true;
                this._cdr.markForCheck();
            },
        });
    }

    toggleRun(run: GableWorkflowRun): void {
        const next = new Set(this.expandedRunIds);
        if (!next.delete(run.id)) {
            next.add(run.id);
        }
        this.expandedRunIds = next;
        this._cdr.markForCheck();
    }

    isRunExpanded(run: GableWorkflowRun): boolean {
        return this.expandedRunIds.has(run.id);
    }

    runIsInFlight(run: GableWorkflowRun): boolean {
        return isInFlight(run);
    }

    runWhen(run: GableWorkflowRun): string {
        return formatInTimezone(run.startedAt ?? run.createdAt, this.timezone) || 'Not started';
    }

    runWhenRelative(run: GableWorkflowRun): string {
        return relativeTime(run.startedAt ?? run.createdAt);
    }

    runOutcome(run: GableWorkflowRun): string {
        if (this.runIsInFlight(run)) {
            return inFlightText(run);
        }
        return runOutcomeText(run);
    }

    /** The row's line under the date. A failed run's badge already says "Failed", so the words give only the reason. */
    runLine(run: GableWorkflowRun): string {
        return run.status === 'failed' ? (run.error ?? '').trim() : this.runOutcome(run);
    }

    runDuration(run: GableWorkflowRun): string {
        return formatDuration(run.durationMs);
    }

    runTriggerLabel(run: GableWorkflowRun): string {
        return (run.triggerLabel ?? '').trim();
    }

    /** A plain click on an in-app run link closes the drawer so the page it lands on is not behind it; modifier clicks open a tab and leave it be. */
    /** App paths route in place and close the drawer; a modified click or an outside link keeps the browser's own behaviour. */
    onRunLinkClick(event: MouseEvent, url: string | null): void {
        if (!url || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
            return;
        }
        if (this.isAppUrl(url)) {
            event.preventDefault();
            this._drawer.close();
            void this._router.navigateByUrl(url);
        }
    }

    runTriggerUrl(run: GableWorkflowRun): string | null {
        return (run.triggerUrl ?? '').trim() || null;
    }

    runDocumentUrl(run: GableWorkflowRun): string | null {
        return (run.documentUrl ?? '').trim() || null;
    }

    isAppUrl(url: string | null): boolean {
        return !!url && url.startsWith('/app');
    }

    runStatusLabel(run: GableWorkflowRun): string {
        if (run.status === 'completed') { return 'Completed'; }
        if (run.status === 'failed') { return 'Failed'; }
        if (run.status === 'running') { return 'Running'; }
        return isWaitingOnCapacity(run) ? 'Waiting' : 'Queued';
    }

    openActivity(): void {
        this._drawer.openActivity('all');
    }

    togglePause(w: GableWorkflow): void {
        const paused = w.status === 'paused';
        const request$ = paused ? this._workflows.resume(w.id) : this._workflows.pause(w.id);
        this._run(w, request$, paused ? 'Workflow resumed' : 'Workflow paused', ({ workflow }) => this._replace(workflow));
    }

    runNow(w: GableWorkflow): void {
        if (!this.isSuperUser) {
            return;
        }
        this._run(w, this._workflows.runNow(w.id), null, ({ run }) => {
            this._replace({ ...w, runs: [run, ...(w.runs ?? []).filter((r) => r.id !== run.id)] });
            if (this.runsWorkflow?.id === w.id) {
                this.runs = [run, ...this.runs.filter((r) => r.id !== run.id)];
                this.runsTotal += 1;
            }
            if (isInFlight(run)) {
                this._growler.success('Run started', w.title);
                this._drawer.watchWorkflowRun(run.id, w.id, w.title);
                return;
            }
            this._drawer.announceWorkflowRun(run, w.title);
        });
    }

    confirmRemove(w: GableWorkflow): void {
        this.removingWorkflow = w;
        this.removeConfirmOpen = true;
        this._cdr.markForCheck();
    }

    onRemoveConfirmClosed(confirmed: boolean): void {
        this.removeConfirmOpen = false;
        const w = this.removingWorkflow;
        this.removingWorkflow = null;
        if (!confirmed || !w) {
            this._cdr.markForCheck();
            return;
        }
        this._run(w, this._workflows.remove(w.id), 'Workflow deleted', () => {
            this.workflows = this.workflows.filter((x) => x.id !== w.id);
            if (this.runsWorkflow?.id === w.id) {
                this.backToList();
            }
        });
    }

    trackById(_index: number, item: { id: number }): number {
        return item.id;
    }

    private _run<T>(w: GableWorkflow, request$: Observable<T>, successTitle: string | null, onSuccess: (res: T) => void): void {
        if (this._busy.has(w.id)) {
            return;
        }
        this._busy.add(w.id);
        this._cdr.markForCheck();
        request$.pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this._busy.delete(w.id);
                if (successTitle) {
                    this._growler.success(successTitle, w.title);
                }
                onSuccess(res);
                this._cdr.markForCheck();
            },
            error: (err: HttpErrorResponse) => {
                this._busy.delete(w.id);
                this._growler.fromErrorResponse(err, 'Could not update the workflow.');
                this._cdr.markForCheck();
            },
        });
    }

    private _replace(workflow: GableWorkflow): void {
        this.workflows = this.workflows.map((x) => (x.id === workflow.id ? workflow : x));
        if (this.runsWorkflow?.id === workflow.id) {
            this.runsWorkflow = workflow;
        }
    }

    /** Runs the server already reports in flight are handed to the drawer too, so a refresh mid-run still ends in a toast. */
    private _watchInFlightRuns(): void {
        for (const w of this.workflows) {
            const latest = latestRun(w);
            if (isInFlight(latest)) {
                this._drawer.watchWorkflowRun(latest.id, w.id, w.title);
            }
        }
    }

    private _focusView(selector = '.back-link'): void {
        this._cdr.markForCheck();
        setTimeout(() => focusWhenMounted(() => this._el.nativeElement.querySelector<HTMLElement>(selector)));
    }
}
